import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';
import { useLocalizedSpelling } from '../hooks/useLocalizedSpelling';

export function NeighborhoodHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['neighborhoodhero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['neighborhoodhero.cta_link'];
  const localize = useLocalizedSpelling();
  return (
    <>
      <HeroSolidBGView
        {...props}
        title={localize(props.title ?? 'Neighborhood')}
        subtitle={props.subtitle && localize(props.subtitle)}
        prefix="neighborhoodhero"
        bgDefinition={bgDefinition}
      />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="neighborhoodhero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="neighborhoodhero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="neighborhoodhero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="neighborhoodhero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="neighborhoodhero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHero',
  displayName: 'Neighborhood Hero',
  tokens: {
    'neighborhoodhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Neighborhood</p>',
    },
    'neighborhoodhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Neighborhood Guide for Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodhero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
    },
  },
  slotGroups: {
    'neighborhoodhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Neighborhood view',
        },
      },
      generator: {
        prompt:
          "Pick the hero background photo for the Neighborhood page: a photo of this property's surrounding neighborhood, such as a streetscape, local landmark, or nearby local-area scene. Write alt text describing what the photo shows.",
        context:
          'Full-bleed background hero at the top of the Neighborhood page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to the local area first, image quality second, uniqueness third. Only fall back to a property exterior if no local-area photo exists. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero — do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'neighborhoodhero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(NeighborhoodHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodHero;
